/**
 * `lighthouse://` links coming back from the system browser.
 *
 * Google refuses OAuth inside embedded browsers, so the desktop app sends
 * sign-in and inbox / calendar connects to the system browser. The callback
 * page there forwards to a `lighthouse://` link, which the shell hands to
 * this window (`bridge.auth.onDeepLink`).
 *
 * A flow that is waiting registers a matcher with `waitForDesktopDeepLink`.
 * Links nobody is waiting for (the window reloaded mid-flow, the creator came
 * back to the app before finishing in the browser) go to the fallback
 * handler installed by init.ts, which still completes the job — but only for
 * a flow this device started recently (`takePendingDesktopFlow`). Any web page
 * can open a `lighthouse://` link, so an unsolicited one must never sign the
 * app in or connect a mailbox.
 */
import { getDesktopBridge } from './bridge'
import type { Unsubscribe } from './types'

export const DESKTOP_AUTH_CALLBACK = 'lighthouse://auth/callback'
export const DESKTOP_OAUTH_CALLBACK = 'lighthouse://oauth-callback'

const FLOW_TIMEOUT_MS = 5 * 60 * 1000
/** How long after a flow starts a late return may still complete it (OAuth codes live ~10 minutes). */
const PENDING_FLOW_MAX_AGE_MS = 10 * 60 * 1000
/** localStorage, so a flow survives the window reloading mid-flow: `{ [kind]: startedAt }`. */
const PENDING_FLOWS_KEY = 'lh-desktop-pending-flows'
/**
 * After the creator comes back to the app without the link arriving, how long
 * to wait before treating the flow as abandoned (the shell focuses the window
 * a moment before it delivers the link).
 */
const RETURN_GRACE_MS = 2500

interface Waiter {
    matches: (url: string) => boolean
    resolve: (url: string) => void
}

const waiters = new Set<Waiter>()
/** Flows in progress, by kind (`auth`, `oauth`), so starting one again replaces the old one. */
const flows = new Set<{ kind: string; cancel: () => void }>()
let fallback: ((url: string) => void) | null = null
let unsubscribe: Unsubscribe | null = null

export function isDesktopAuthCallback(url: string): boolean {
    return url.startsWith(DESKTOP_AUTH_CALLBACK)
}

export function isDesktopOAuthCallback(url: string): boolean {
    return url.startsWith(DESKTOP_OAUTH_CALLBACK)
}

function readPendingFlows(): Record<string, number> {
    try {
        const parsed = JSON.parse(localStorage.getItem(PENDING_FLOWS_KEY) || '{}')
        return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
        return {}
    }
}

function writePendingFlows(flows: Record<string, number>): void {
    try {
        if (Object.keys(flows).length) localStorage.setItem(PENDING_FLOWS_KEY, JSON.stringify(flows))
        else localStorage.removeItem(PENDING_FLOWS_KEY)
    } catch {
        // Private mode: late returns just won't complete.
    }
}

function setPendingFlow(kind: string, startedAt: number | null): void {
    const flows = readPendingFlows()
    if (startedAt === null) delete flows[kind]
    else flows[kind] = startedAt
    writePendingFlows(flows)
}

/**
 * True (once) when this device started a flow of this kind in the last few
 * minutes that no link has completed yet: the only case a late link is for us.
 */
export function takePendingDesktopFlow(kind: string, now = Date.now()): boolean {
    const startedAt = readPendingFlows()[kind]
    setPendingFlow(kind, null)
    return typeof startedAt === 'number' && now >= startedAt && now - startedAt < PENDING_FLOW_MAX_AGE_MS
}

function dispatch(url: string): void {
    if (typeof url !== 'string') return
    for (const w of waiters) {
        if (w.matches(url)) {
            w.resolve(url)
            return
        }
    }
    fallback?.(url)
}

/** Subscribe to the shell's deep links (idempotent). */
export function startDesktopDeepLinks(onUnclaimed?: (url: string) => void): boolean {
    const bridge = getDesktopBridge()
    if (!bridge) return false
    if (onUnclaimed) fallback = onUnclaimed
    if (!unsubscribe) unsubscribe = bridge.auth.onDeepLink(dispatch)
    return true
}

/**
 * Open `url` in the system browser and resolve with the first deep link that
 * `matches`. Rejects with `cancelled` when the creator returns to the app and
 * no link follows, when a newer flow of the same kind starts, or on timeout.
 */
export function waitForDesktopDeepLink(
    url: string,
    matches: (link: string) => boolean,
    opts: { kind: string; timeoutMs?: number } = { kind: 'oauth' },
): Promise<string> {
    const bridge = getDesktopBridge()
    if (!bridge) return Promise.reject(new Error('The desktop app is not available.'))
    startDesktopDeepLinks()
    cancelFlow(opts.kind)
    // Cleared when the link arrives here; kept when the wait ends early, so a late return still counts.
    setPendingFlow(opts.kind, Date.now())

    return new Promise<string>((resolve, reject) => {
        let left = false
        let graceTimer: ReturnType<typeof setTimeout> | undefined
        const timeout = setTimeout(() => finish(new Error('The request timed out. Please try again.')), opts.timeoutMs ?? FLOW_TIMEOUT_MS)
        const onBlur = () => {
            left = true
            if (graceTimer) clearTimeout(graceTimer)
        }
        const onFocus = () => {
            if (!left) return
            if (graceTimer) clearTimeout(graceTimer)
            graceTimer = setTimeout(() => finish(new Error('cancelled')), RETURN_GRACE_MS)
        }
        const waiter: Waiter = { matches, resolve: (link) => finish(null, link) }
        const flow = { kind: opts.kind, cancel: () => finish(new Error('cancelled')) }

        function finish(err: Error | null, link?: string) {
            if (!waiters.has(waiter)) return
            waiters.delete(waiter)
            flows.delete(flow)
            clearTimeout(timeout)
            if (graceTimer) clearTimeout(graceTimer)
            window.removeEventListener('blur', onBlur)
            window.removeEventListener('focus', onFocus)
            if (err) {
                reject(err)
                return
            }
            setPendingFlow(flow.kind, null)
            resolve(link!)
        }

        waiters.add(waiter)
        flows.add(flow)
        window.addEventListener('blur', onBlur)
        window.addEventListener('focus', onFocus)
        try {
            bridge.auth.openInBrowser(url)
        } catch (err) {
            setPendingFlow(opts.kind, null)
            finish(err instanceof Error ? err : new Error('Could not open your browser.'))
        }
    })
}

/** Abandon a waiting flow of this kind (a second click restarts it cleanly). */
function cancelFlow(kind: string): void {
    for (const f of [...flows]) if (f.kind === kind) f.cancel()
}

/** Test hook. */
export function resetDesktopDeepLinks(): void {
    for (const f of [...flows]) f.cancel()
    waiters.clear()
    flows.clear()
    unsubscribe?.()
    unsubscribe = null
    fallback = null
    writePendingFlows({})
}

/** Test hook: deliver a link as the shell would. */
export function deliverDeepLinkForTest(url: string): void {
    dispatch(url)
}
